import React, { useEffect, useMemo, useState } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'

const INSTALL_DISMISS_KEY = 'nixia:pwa:install:dismissed_until'

const DAY_MS = 24 * 60 * 60 * 1000
const DEFAULT_DISMISS_DAYS = 7

const isIos = () => {
  if (typeof navigator === 'undefined') return false
  return /iphone|ipad|ipod/i.test(navigator.userAgent)
}

const isInStandaloneMode = () => {
  if (typeof window === 'undefined') return false
  const standaloneMatch = window.matchMedia?.('(display-mode: standalone)')?.matches
  // iOS Safari
  const iosStandalone = window.navigator?.standalone === true
  return Boolean(standaloneMatch || iosStandalone)
}

const readDismissedUntil = (key) => {
  try {
    const raw = localStorage.getItem(key)
    const value = raw ? Number(raw) : 0
    return Number.isFinite(value) ? value : 0
  } catch {
    return 0
  }
}

const writeDismissedUntil = (key, days = DEFAULT_DISMISS_DAYS) => {
  try {
    localStorage.setItem(key, String(Date.now() + days * DAY_MS))
  } catch {
    // ignore
  }
}

const shouldHideByDismiss = (key) => readDismissedUntil(key) > Date.now()

export default function PwaManager() {
  useRegisterSW({
    immediate: true
  })

  const [installed, setInstalled] = useState(() => isInStandaloneMode())
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [showIosHint, setShowIosHint] = useState(false)
  const [installHidden, setInstallHidden] = useState(() => shouldHideByDismiss(INSTALL_DISMISS_KEY))

  useEffect(() => {
    const onDisplayModeChange = () => setInstalled(isInStandaloneMode())
    const mql = window.matchMedia?.('(display-mode: standalone)')
    mql?.addEventListener?.('change', onDisplayModeChange)
    return () => mql?.removeEventListener?.('change', onDisplayModeChange)
  }, [])

  useEffect(() => {
    const onBeforeInstallPrompt = (event) => {
      if (!event) return
      event.preventDefault()
      setDeferredPrompt(event)
    }

    const onAppInstalled = () => {
      setInstalled(true)
      setDeferredPrompt(null)
      setShowIosHint(false)
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    window.addEventListener('appinstalled', onAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
      window.removeEventListener('appinstalled', onAppInstalled)
    }
  }, [])

  useEffect(() => {
    if (installed) {
      setShowIosHint(false)
      return
    }
    if (!isIos()) return

    // iOS Safari doesn't fire beforeinstallprompt; show a lightweight hint instead
    setShowIosHint(true)
  }, [installed])

  const canOfferInstall = useMemo(() => {
    if (installed) return false
    if (installHidden) return false
    return Boolean(deferredPrompt || showIosHint)
  }, [deferredPrompt, installHidden, installed, showIosHint])

  const handleInstall = async () => {
    if (!deferredPrompt) return
    try {
      deferredPrompt.prompt()
      await deferredPrompt.userChoice
    } finally {
      setDeferredPrompt(null)
    }
  }

  if (!canOfferInstall) {
    return null
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-sm">
      <div className="rounded-2xl border border-slate-200 bg-white/95 shadow-lg backdrop-blur px-4 py-3">
        <p className="text-sm font-semibold text-slate-900">Installer DiMax</p>
        {showIosHint && !deferredPrompt ? (
          <p className="mt-1 text-[13px] text-slate-600">
            Sur iPhone/iPad : ouvre le menu Partager puis sélectionne « Sur l’écran d’accueil ».
          </p>
        ) : (
          <p className="mt-1 text-[13px] text-slate-600">
            Accède plus vite à Dimax depuis ton écran d’accueil, même comme une application.
          </p>
        )}

        <div className="mt-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              writeDismissedUntil(INSTALL_DISMISS_KEY)
              setInstallHidden(true)
            }}
            className="px-3 py-1.5 text-[13px] rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100"
          >
            Fermer
          </button>
          {deferredPrompt ? (
            <button
              type="button"
              onClick={handleInstall}
              className="px-3 py-1.5 text-[13px] rounded-lg bg-slate-900 text-white hover:bg-slate-800"
            >
              Installer
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
